import React, { useMemo } from "react";
import RailGroup from "./RailGroup";
import { linkedinView, railSituation } from "./railModel.mjs";
import { latestFormSubmission } from "../formAnswers.mjs";

/*
 * THEIR LINKEDIN AND INTAKE (frames 1b and 1d open; the line in 1a, 1c, 1g): their
 * ad account and what our own ads reached them with, both read live from our
 * auditor, and how they arrived.
 *
 * THE AUDITOR IS NOT CONNECTED IN PRODUCTION (AUDITOR_DATABASE_URL is unset), so
 * the state that ships today is one sentence in a dashed box, frame 1d's, with
 * nothing else pretending to be data. Once it is connected, a company with no
 * matching account reads "No ad account linked" with Link account as a Proposed
 * tag, and a linked one its rows: the budget in the account, live campaigns, the
 * last change, the last audit, who manages it, then our ads' reach by impressions
 * and clicks. NEVER SPEND: the account's last spend and the delivery archive's
 * pro-rata cost are both in the payload and neither is printed (the brief).
 *
 * THE INTAKE is one line: the form by its own name and date, or the intro email
 * and who sent it ("Intro email from Dani Avitz, Sep 15 · no form"), and the fixed
 * "Campaign: not captured", because campaign-level attribution never was. Older
 * records also carry the frozen HubSpot export (first form, first page, visits,
 * the old deal), shown under its own name and only where it holds something.
 *
 * Props:
 *   record   the shell's bundle: act (ads, delivery, audit_line, auditor_connected),
 *            contact (lead_channel, referrer_name, created_at, properties),
 *            company, key (the situation), timeline.rows (the form)
 *   ops      unused: nothing here writes (Link account is Proposed)
 *   state    "open" | "summary"
 *   sheet    boolean, phone
 */

function Rows({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="rec-kv rec-kv--96 rec-li__rows">
      {rows.map((r) => (
        <React.Fragment key={r.key}>
          <span>{r.label}</span>
          <span>
            {r.value}
            {r.sub && <span className="rec-sub"> · {r.sub}</span>}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function TheirLinkedIn({ record, state = "summary", sheet = false }) {
  const form = useMemo(() => latestFormSubmission(record.timeline.rows), [record.timeline.rows]);
  const view = linkedinView({ act: record.act, contact: record.contact, form, situation: railSituation(record.view), now: record.now });
  const company = record.company;

  return (
    <RailGroup id="linkedin" title="Their LinkedIn and intake" summary={view.summary} defaultOpen={state === "open"} sheet={sheet}>
      {view.state === "not_connected" && (
        <div className="rec-dashedbox rec-dashedbox--line">
          Ad account and our ads’ reach show here once our auditor is connected. Not connected in production yet.
        </div>
      )}
      {view.state === "no_account" && (
        <div className="rec-dashedbox">
          <span className="rec-strong">No ad account linked</span>
          <span className="rec-sub">No ad account in our auditor matches {company ? company.name : "this company"}.</span>
          <span className="rec-proposed-wrap">
            <span className="rec-inert">Link account</span>
            <span className="rec-proposed">Proposed</span>
          </span>
        </div>
      )}
      {view.state === "linked" && <Rows rows={view.accountRows} />}
      <Rows rows={view.reachRows} />

      <Rows
        rows={[
          { key: "intake", label: view.intake.label, value: view.intake.text },
          { key: "attribution", label: "Attribution", value: "Campaign: not captured" },
        ]}
      />

      {view.hubspotRows.length > 0 && (
        <div className="rec-li__hubspot">
          <span className="rec-label">HubSpot export (frozen)</span>
          <Rows rows={view.hubspotRows} />
        </div>
      )}
    </RailGroup>
  );
}
